import { useState, useRef, useEffect, useLayoutEffect, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Portal } from '../portal'

export interface DropdownOption {
  value: string
  label: string
  description?: string
  icon?: ReactNode
  /** If true, shows as locked and triggers onLockedClick instead of onChange */
  locked?: boolean
  /** Badge element to show on the right (e.g., LockBadge) */
  badge?: ReactNode
}

interface DropdownProps {
  label?: string
  value: string
  onChange: (value: string) => void
  options: DropdownOption[]
  className?: string
  /** Adds a subtle gradient border glow effect */
  glow?: boolean
  /** Called when a locked option is clicked */
  onLockedClick?: (value: string) => void
  /** Disable the dropdown */
  disabled?: boolean
}

interface MenuPosition {
  /** When set, anchors the menu's top edge in fixed coords. */
  top?: number
  /** When set, anchors the menu's bottom edge in fixed coords. */
  bottom?: number
  left: number
  width: number
  maxHeight: number
}

export function Dropdown({
  label,
  value,
  onChange,
  options,
  className,
  glow,
  onLockedClick,
  disabled = false,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [menuPos, setMenuPos] = useState<MenuPosition | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const selected = options.find((o) => o.value === value)

  // Close on click outside. Menu is portaled to <body>, so clicks on
  // menu items are NOT descendants of `containerRef` — we have to
  // check `menuRef` separately or the menu would dismiss before its
  // own click handlers fire.
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      const inContainer = containerRef.current?.contains(target) ?? false
      const inMenu = menuRef.current?.contains(target) ?? false
      if (!inContainer && !inMenu) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Compute the menu's fixed-coord rectangle when it opens. We portal
  // the menu to <body> so it escapes any clipping `overflow-hidden`
  // ancestors (e.g. the Advanced Options collapse animation) — that's
  // why we need the absolute viewport coordinates here.
  //
  // We also pick the side (above vs below the button) based on
  // available viewport space so a long list never gets cut off, and
  // reposition on scroll/resize so the menu sticks to the trigger.
  useLayoutEffect(() => {
    if (!isOpen) return
    const btn = buttonRef.current
    if (!btn) return

    const recompute = () => {
      const rect = btn.getBoundingClientRect()
      const viewportH = window.innerHeight
      const margin = 8 // breathing room from viewport edge
      const gap = 4
      const spaceBelow = viewportH - rect.bottom - margin
      const spaceAbove = rect.top - margin
      const upward = spaceBelow < 240 && spaceAbove > spaceBelow
      setMenuPos({
        left: rect.left,
        width: rect.width,
        top: upward ? undefined : rect.bottom + gap,
        bottom: upward ? viewportH - rect.top + gap : undefined,
        maxHeight: Math.max(160, upward ? spaceAbove : spaceBelow),
      })
      // If the trigger scrolled fully out of view, dismiss instead of
      // floating an orphaned menu.
      if (rect.bottom < 0 || rect.top > viewportH) {
        setIsOpen(false)
      }
    }

    recompute()

    // Reposition on scroll/resize. Capture-phase listener catches scrolls
    // anywhere on the page — but we explicitly skip scroll events that
    // originate INSIDE the menu so scrolling the option list doesn't
    // re-fire (or worse, dismiss) the menu.
    const handleScroll = (e: Event) => {
      const target = e.target as Node | null
      if (target && menuRef.current?.contains(target)) return
      recompute()
    }
    const handleResize = () => recompute()
    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleResize)
    }
  }, [isOpen])

  return (
    <div ref={containerRef} className="relative h-full w-full">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          "relative flex flex-col justify-between px-3 py-2 md:px-4 md:py-3 rounded-xl text-white min-w-[140px] w-full h-full transition-all duration-300",
          glow 
            ? "bg-[#0d0d0d] shadow-[0_0_15px_-3px_rgba(139,92,246,0.4)]"
            : "bg-white/[0.03] hover:bg-white/[0.05]",
          disabled && "opacity-50 cursor-not-allowed",
          className
        )}
      >
        {/* Top row: Label left, Chevron right */}
        <div className="flex items-center justify-between">
          {label && <span className="text-xs text-[#666]">{label}</span>}
          <ChevronDown
            className={cn("size-4 text-[#666] transition-transform shrink-0", isOpen && "rotate-180")}
          />
        </div>
        
        {/* Value row with icon */}
        <div className="flex items-center gap-2">
          {/* Hide icon on mobile */}
          {selected?.icon && <span className="hidden md:block text-[#888]">{selected.icon}</span>}
          <span className="text-sm">{selected?.label}</span>
        </div>
      </button>

      {/* Dropdown menu — portaled to <body> so it escapes any clipping
          ancestor (e.g. the Advanced Options collapse panel which uses
          double `overflow-hidden` for its grid-rows animation). Bounded
          height with internal scroll for long option lists. */}
      {isOpen && menuPos && (
        <Portal>
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              left: menuPos.left,
              width: menuPos.width,
              top: menuPos.top,
              bottom: menuPos.bottom,
              maxHeight: `min(${menuPos.maxHeight}px, 320px)`,
              zIndex: 100,
            }}
            className="rounded-xl bg-[#111] border border-white/[0.1] shadow-xl overflow-y-auto overscroll-contain"
          >
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  if (option.locked && onLockedClick) {
                    onLockedClick(option.value)
                    setIsOpen(false)
                  } else if (!option.locked) {
                    onChange(option.value)
                    setIsOpen(false)
                  }
                }}
                className={cn(
                  "w-full px-4 py-2.5 text-left transition-colors flex items-center gap-2.5",
                  value === option.value
                    ? "bg-white/[0.08] text-white"
                    : "text-[#888] hover:bg-white/[0.05] hover:text-white",
                  option.locked && "opacity-70"
                )}
              >
                {/* Hide icon on mobile in dropdown menu too */}
                {option.icon && <span className="hidden md:block shrink-0">{option.icon}</span>}
                <div className="flex-1">
                  <div className="text-sm">{option.label}</div>
                  {option.description && (
                    <div className="text-xs text-[#555]">{option.description}</div>
                  )}
                </div>
                {/* Badge (lock icon) */}
                {option.badge && <span className="shrink-0">{option.badge}</span>}
              </button>
            ))}
          </div>
        </Portal>
      )}
    </div>
  )
}
